import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  effect,
  inject,
  signal,
  untracked,
  viewChild
} from '@angular/core';

import { ButtonModule } from 'primeng/button';
import { LucideAngularModule } from 'lucide-angular';

import { GuidedTourService } from '../../services/guided-tour.service';
import { GuidedTourTargetResolverService } from '../../services/guided-tour-target-resolver.service';
import { EMPTY_TOUR_LAYOUT, TourLayout, computeTourLayout } from './guided-tour-layout.helper';

const REVALIDATE_INTERVAL_MS = 400;
const DEFAULT_POPOVER_HEIGHT = 190;

/**
 * Host global do tour (montado uma única vez no Layout): backdrop, recorte,
 * balão e ciclo de vida (foco, Esc, scroll/resize). Nunca aciona o alvo.
 */
@Component({
  selector: 'app-guided-tour-host',
  standalone: true,
  imports: [ButtonModule, LucideAngularModule],
  templateUrl: './guided-tour-host.component.html',
  styleUrl: './guided-tour-host.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GuidedTourHostComponent implements OnDestroy {
  protected readonly tour = inject(GuidedTourService);
  private readonly resolver = inject(GuidedTourTargetResolverService);
  private readonly zone = inject(NgZone);
  private readonly popover = viewChild<ElementRef<HTMLElement>>('popover');

  protected readonly layout = signal<TourLayout>(EMPTY_TOUR_LAYOUT);

  private frame: number | null = null;

  constructor() {
    effect(() => {
      const step = this.tour.currentStep();
      untracked(() => {
        if (!step) return;
        this.revealTarget(step.targetId);
        this.reposition();
        setTimeout(() => this.focusPopover());
      });
    });

    // Listeners e polling fora da zona: não mantêm a app "instável" nem disparam CD sem mudança.
    effect((onCleanup) => {
      if (!this.tour.isActive()) return;
      const onScroll = () => this.scheduleReposition();
      let interval: ReturnType<typeof setInterval>;
      this.zone.runOutsideAngular(() => {
        document.addEventListener('scroll', onScroll, true);
        interval = setInterval(() => this.revalidateTarget(), REVALIDATE_INTERVAL_MS);
      });
      onCleanup(() => {
        document.removeEventListener('scroll', onScroll, true);
        clearInterval(interval);
      });
    });
  }

  @HostListener('window:resize')
  protected onResize(): void {
    this.scheduleReposition();
  }

  @HostListener('document:keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (!this.tour.isActive()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.tour.skip();
      return;
    }
    if (event.key === 'Tab') this.trapFocus(event);
  }

  ngOnDestroy(): void {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.tour.cancel();
  }

  private scheduleReposition(): void {
    if (this.frame !== null) return;
    this.zone.runOutsideAngular(() => {
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.reposition();
      });
    });
  }

  private reposition(): void {
    const step = this.tour.currentStep();
    if (!step) return;
    const target = this.resolver.resolve(step.targetId);
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const popoverHeight = this.popover()?.nativeElement.offsetHeight || DEFAULT_POPOVER_HEIGHT;
    const next = computeTourLayout(
      { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
      { width: window.innerWidth, height: window.innerHeight },
      popoverHeight
    );
    if (JSON.stringify(next) === JSON.stringify(this.layout())) return;
    this.zone.run(() => this.layout.set(next));
  }

  private revalidateTarget(): void {
    const step = this.tour.currentStep();
    if (!step) return;
    if (!this.resolver.resolve(step.targetId)) {
      this.zone.run(() => this.tour.dropStep(step.id));
      return;
    }
    this.reposition();
  }

  private revealTarget(targetId: string): void {
    const target = this.resolver.resolve(targetId);
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const visible = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (visible) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({
      block: 'center',
      inline: 'nearest',
      behavior: reduced ? 'auto' : 'smooth'
    });
  }

  private focusPopover(): void {
    this.popover()?.nativeElement.focus({ preventScroll: true });
  }

  private trapFocus(event: KeyboardEvent): void {
    const container = this.popover()?.nativeElement;
    if (!container) return;
    const focusable = Array.from(
      container.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )
    );
    if (focusable.length === 0) {
      event.preventDefault();
      container.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const outside = !container.contains(active) || active === container;
    if (event.shiftKey && (active === first || outside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault();
      first.focus();
    }
  }
}
